<template>
	<slot name="trigger" :open-drawer="openDrawer" :close-drawer="closeDrawer" :toggle-drawer="toggleDrawer"></slot>
	<Teleport to="#app">
		<div class="fixed z-50 inset-0" v-cloak v-if="drawerOpen"></div>
		<Transition
			enter-from-class="translate-x-full"
			leave-to-class="translate-x-full"
			enter-active-class="transition-transform duration-500 ease-out"
			leave-active-class="transition-transform duration-500 ease-in"
		>
			<div ref="drawer" class="fixed z-50 inset-y-0 right-0 w-full max-w-screen-sm focus-visible:outline-none after:absolute after:inset-0 after:pointer-events-none after:outline-0 focus-visible:after:outline-1 after:outline-dashed after:outline-gold-400/50 after:-outline-offset-4" v-cloak v-if="drawerOpen" v-click-outside="closeDrawer" @click.capture="handleClick" tabindex="-1">
				<slot name="content" :open-drawer="openDrawer" :close-drawer="closeDrawer" :toggle-drawer="toggleDrawer" :dropdown-open="dropdownOpen" :open-dropdown="openDropdown" :close-dropdown="closeDropdown"></slot>
			</div>
		</Transition>
	</Teleport>
</template>

<script setup>
import { ref, watch, onUnmounted, nextTick } from "vue";
import { lock, unlock } from "@js/body-scroll-lock.js";
import { create, activate, deactivate } from "@js/focus-trap.js";

const drawer = ref(null);
const focusTrap = ref(null);
const drawerOpen = ref(false);

function openDrawer() {
	drawerOpen.value = true;
}

function closeDrawer() {
	drawerOpen.value = false;
}

function toggleDrawer() {
	drawerOpen.value = !drawerOpen.value;
}

watch(drawerOpen, async (newValue) => {
	if (newValue) {
		lock();
		await nextTick();
		focusTrap.value = create(drawer.value);
		activate(focusTrap.value);
	} else {
		unlock();
		if (focusTrap.value) {
			deactivate(focusTrap.value);
		}
	}
}, { immediate: true });

function handleKeydown(e) {
	if (e.key === 'Escape' && drawerOpen.value) {
		closeDrawer();
	}
}

window.addEventListener('keydown', handleKeydown);

onUnmounted(() => {
	window.removeEventListener('keydown', handleKeydown);
});

const dropdownOpen = ref('');

function openDropdown(key) {
	dropdownOpen.value = key;
}

function closeDropdown() {
	dropdownOpen.value = '';
}

const handleClick = async (e) => {
	// Check if this is an artificial click event
	if (e.isTrusted === false) {
		return // Skip handling programmatic clicks
	}

	// Stop the original click event
	e.stopPropagation()
	e.preventDefault()

	const isClickable = e.target.closest('a, button');
	const isAnchorLink = isClickable && isClickable.tagName === 'A' && isClickable.getAttribute('href').startsWith('#');

	if (isAnchorLink) {
		closeDrawer()
		// Wait for drawer to partly close before proceeding
		await new Promise(resolve => setTimeout(resolve, 300));
	}

	// Get the original target
	const target = e.target
	target.click()
}
</script>
